import React, { createContext, useContext, useEffect, useRef, ReactNode, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { useOrganizer } from './OrganizerContext';
import { useGeolocation, useLocationStore } from '../stores/locationStore';
import { AnalyticsService } from '../services/AnalyticsService';
import { ZoneGeographiqueService } from '../services/ZoneGeographiqueService';

/**
 * Lire la géoloc fraîche depuis le store Zustand (hors closure React)
 * Résout le problème de closure stale dans stableContextValue
 */
const getFreshUserLocation = () => {
  const location = useLocationStore.getState().location;
  return location ? ZoneGeographiqueService.creerLocalisationUtilisateur(location) : null;
};

interface AnalyticsContextType {
  // Tracking des événements (Phase 1 - Touristes)
  trackEventAppearance: (event: any, viewInterface: 'card' | 'carte_pin' | 'carte_preview', durationMs?: number) => void;
  trackEventClick: (event: any, viewInterface: 'card' | 'carte_pin' | 'carte_preview', timeSinceAppearance?: number) => void;
  trackEventConsultation: (event: any, duration: number, consultationDetails: any) => void;
  trackEventLike: (event: any, method: 'swipe_droite' | 'bouton_favori', context: 'card' | 'carte' | 'modale' | 'carte_preview') => void;
  trackEventDismiss: (event: any, context?: 'card' | 'carte' | 'modale' | 'carte_preview') => void;
  trackNavigation: (event: any, type: 'directions' | 'website' | 'phone' | 'email' | 'calendar', context: 'card' | 'carte' | 'modale' | 'carte_preview') => void;
  trackSwipeAction: (event: any, type: 'swipe_droite' | 'swipe_gauche', context: 'card' | 'carte' | 'modale' | 'carte_preview') => void;
  trackPinClick: (pinLocation: { lat: number; lng: number }, eventsInPin: any[]) => void;
  
  // Tracking des actions utilisateur (Phase 1)
  markSearchPerformed: () => void;
  markFilterApplied: () => void;
  trackSearch: (searchTerms: string, resultCount: number) => void;
  trackFilters: (filters: any, resultCount: number) => void;

  // Tracking organisateur - Création d'événements (Phase 2)
  trackCreationEventStart: (typeEvenement: 'unique' | 'recurrent', estPremierEvenement: boolean, aDraftsExistants: boolean) => void;
  trackFormStepCompleted: (etape: 'basic_info' | 'details' | 'preview', tempsPasseEtape: number, champsRemplis: string[], erreursRencontrees: string[], completudePourcentage: number, typeEvenement?: 'unique' | 'recurrent') => void;
  trackFieldModified: (etape: 'basic_info' | 'details' | 'preview', champ: string, nombreModifications: number, valeurLongueur?: number, tentativesGeolocalisation?: number) => void;
  trackCreationAbandoned: (etapeAbandon: 'basic_info' | 'details' | 'preview', dernierChampModifie: string, tempsTotalSession: number, completudeFinale: number, raisonProbable: 'erreur_validation' | 'fermeture_navigateur' | 'inactivite', champsRemplis: string[]) => void;
  trackRecurringEventConfigured: (frequence: 'daily' | 'weekly', typeFin: 'date' | 'occurrences', dureeSerie: number, nombreJoursSemaine?: number, nombreOccurrences?: number) => void;
  trackDraftSaved: (draftId: string, etapeSauvegarde: 'basic_info' | 'details' | 'preview', completudePourcentage: number, champsRemplis: string[], estPremierDraft: boolean, tempsAvantSauvegarde: number) => void;
  trackEventPublished: (evenementId: string, tempsTotalCreation: number, nombreSessionsCreation: number, etaitDraft: boolean, completudeFinale: number, typeEvenement: 'unique' | 'recurrent', nombreOccurrences?: number) => void;
  trackOrganizerNavigation: (pageVisitee: 'events_actifs' | 'drafts' | 'events_passes' | 'profil' | 'statistiques' | 'creation', tempsPasse: number, actionsEffectuees: string[]) => void;
  trackFailedPublishAttempt: (etape: 'basic_info' | 'details' | 'preview', champsMananquants: string[], completude: number, tempsAvantTentative: number, nombreTentatives: number) => void;

  // Tracking UX : Vue, Partage, Affordance, Navigation
  trackViewModeChange: (newMode: 'list' | 'map', previousMode: 'list' | 'map') => void;
  trackSwipeAffordanceShown: (context: 'card' | 'modal') => void;
  trackSwipeAfterAffordance: (context: 'card' | 'modal', didSwipe: boolean) => void;
  trackBottomNavClick: (item: string, action: 'open' | 'close' | 'navigate' | 'toggle') => void;
  trackShareEvent: (event: any, method: 'native' | 'whatsapp' | 'clipboard', context: string) => void;

  // Tracking Location Fallback Toast
  trackLocationFallbackToastShown: () => void;
  trackLocationFallbackToastAction: (action: 'locate_me' | 'dismiss' | 'auto_dismiss', result?: 'geolocation_success' | 'geolocation_denied') => void;

  // Tracking profil organisateur (Phase 4)
  trackProfileCompletionScore: (elementsCompletes: string[], elementsManquants: string[], score: number) => void;
  trackPublicProfileView: (organisateurConsulteId: string, elementsVisibles: string[], tempsConsultation: number) => void;
  trackContactClick: (organisateurId: string, typeContact: 'email' | 'telephone' | 'site_web' | 'instagram', depuisVue: 'profil_public' | 'evenement_modal' | 'card_evenement') => void;
  trackImageUpload: (typeImage: 'avatar' | 'galerie', tailleFichier: number, formatImage: string, succesUpload: boolean, nombreImagesGalerieTotal?: number, tentativeNumero?: number) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const organizerContext = useOrganizer();
  const geolocation = useGeolocation();
  
  // ✅ UNIQUE : Une seule instance d'analytics pour toute l'app
  const isInitialized = useRef(false);
  const lastUserContext = useRef<string>('');
  const lastGeolocation = useRef<string>('');
  const sessionInitialized = useRef(false);
  
  // ✅ NOUVEAU : Debounce global 1.5s pour éviter le spam
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // ✅ FONCTION DEBOUNCE : Évite le spam analytics avec délai 1.5s
  const debounceAnalytics = useCallback((key: string, fn: () => void, delay: number = 1500) => {
    // Annuler le timer précédent s'il existe
    const existingTimer = debounceTimers.current.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // Créer un nouveau timer
    const newTimer = setTimeout(() => {
      fn();
      debounceTimers.current.delete(key);
    }, delay);
    
    debounceTimers.current.set(key, newTimer);
  }, []);

  // ✅ Initialiser le service analytics UNE SEULE FOIS
  useEffect(() => {
    if (!isInitialized.current) {
      // Logger.listener();
      AnalyticsService.initialize();
      isInitialized.current = true;

      // 🧪 DEV: Exposer AnalyticsService pour debug console
      if (process.env.NODE_ENV === 'development') {
        (window as any).AnalyticsService = AnalyticsService;
        console.log('🧪 Debug disponible: window.AnalyticsService');
      }
    }
  }, []);

  // ✅ Mettre à jour le contexte utilisateur uniquement si changé
  useEffect(() => {
    if (!authContext || !isInitialized.current) return;
    
    // ✅ Logique simple : connecté = organisateur, non connecté = touriste
    const typeUtilisateur = authContext.user ? 'organisateur' : 'touriste';
    const statutConnexion = authContext.user ? 'connecte' : 'anonyme';
    const contextKey = `${typeUtilisateur}-${statutConnexion}`;
    
    if (lastUserContext.current !== contextKey) {
      // Logger.analytics();
      AnalyticsService.updateUserContext(typeUtilisateur, statutConnexion);
      lastUserContext.current = contextKey;
    }
  }, [authContext]);

  // ✅ Mettre à jour la géolocalisation uniquement si changée
  useEffect(() => {
    if (!isInitialized.current) return;
    
    const geoKey = geolocation.location 
      ? `${geolocation.location.latitude}-${geolocation.location.longitude}`
      : geolocation.error ? 'error' : 'none';
    
    if (lastGeolocation.current !== geoKey) {
      if (geolocation.location) {
        // Logger.geo();
        AnalyticsService.setUserLocation(geolocation.location);
        AnalyticsService.updateGeolocationPermission('accordee');
      } else if (geolocation.error) {
        // // console.log( - SILENCIEUX EN PROD - REMPLACÉ'❌ Analytics Provider - Géolocalisation refusée');
        AnalyticsService.updateGeolocationPermission('refusee');
      }
      lastGeolocation.current = geoKey;
    }
  }, [geolocation.location, geolocation.error]);

  // ✅ SESSION RESILIENCE PATTERN - Initialiser session SANS attendre géolocalisation
  useEffect(() => {
    console.log('🔍 [AnalyticsProvider] Check session init:', {
      hasLocation: !!geolocation.location,
      sessionInitialized: sessionInitialized.current,
      isInitialized: isInitialized.current
    });
    
    // ✅ NOUVEAU : Initialiser session immédiatement si pas encore fait
    if (isInitialized.current && !sessionInitialized.current) {
      console.log('🚀 [AnalyticsProvider] Initialisation session analytics (resilience mode)...');
      
      // Initialiser avec géolocalisation si disponible, sinon sans
      AnalyticsService.initializeSession(geolocation.location || null);
      
      // Détecter automatiquement le type d'utilisateur
      if (organizerContext?.organizerProfile?.id) {
        AnalyticsService.updateUserContext('organisateur', authContext?.user ? 'connecte' : 'anonyme');
      } else {
        AnalyticsService.updateUserContext('touriste', authContext?.user ? 'connecte' : 'anonyme');
      }
      
      sessionInitialized.current = true;
      console.log('✅ [AnalyticsProvider] Session analytics initialisée (mode résilient) !');
    }
    
    // ✅ ENRICHIR la session si géolocalisation arrive après
    if (geolocation.location && sessionInitialized.current) {
      console.log('🌍 [AnalyticsProvider] Enrichissement session avec géolocalisation...');
      AnalyticsService.enrichSessionWithLocation(geolocation.location);
    }
  }, [geolocation.location, organizerContext?.organizerProfile?.id, authContext?.user, isInitialized.current]);

  // ✅ NOUVEAU : Tracker la fin de session
  useEffect(() => {
    if (!isInitialized.current) return;

    // Fonction pour tracker la fin de session
    const handleBeforeUnload = () => {
      AnalyticsService.trackFinSession();
    };

    // Listener pour fermeture de page/onglet
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup quand le provider se démonte
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Tracker aussi la fin de session au démontage
      AnalyticsService.trackFinSession();
    };
  }, [isInitialized.current]);

  // ✅ NOUVEAU : Tracker l'installation PWA (événement unique)
  useEffect(() => {
    if (!isInitialized.current) return;

    // Écouter l'événement d'installation PWA
    const handleAppInstalled = () => {
      console.log('📲 [AnalyticsProvider] PWA installée !');
      AnalyticsService.trackPWAInstalled('prompt_natif');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInitialized.current]);

  // ===== MÉTHODES PHASE 2 : ANALYTICS ORGANISATEUR =====

  const trackCreationEventStart = (typeEvenement: 'unique' | 'recurrent', estPremierEvenement: boolean, aDraftsExistants: boolean) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    // INITIALISER ou METTRE À JOUR la session
    let sessionContext = AnalyticsService.getSessionContext();
    if (!sessionContext) {
      // Première fois : créer session
      AnalyticsService.initializeSession(geolocation.location);
    }
    
    // TOUJOURS mettre à jour le type d'utilisateur (sans recréer la session)
    AnalyticsService.updateUserContext('organisateur', authContext?.user ? 'connecte' : 'anonyme');
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackCreationEvenementDebut(organizerContext.organizerProfile.id, typeEvenement, estPremierEvenement, aDraftsExistants, userLocation || undefined);
  };

  const trackFormStepCompleted = (etape: 'basic_info' | 'details' | 'preview', tempsPasseEtape: number, champsRemplis: string[], erreursRencontrees: string[], completudePourcentage: number, typeEvenement?: 'unique' | 'recurrent') => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    // VÉRIFIER + INITIALISER une seule fois
    let sessionContext = AnalyticsService.getSessionContext();
    if (!sessionContext) {
      AnalyticsService.initializeSession(geolocation.location);
      AnalyticsService.updateUserContext('organisateur', authContext?.user ? 'connecte' : 'anonyme');
    }
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    const organizerId = organizerContext.organizerProfile.id;
    
    // LOGIQUE CONDITIONNELLE : appeler la bonne méthode selon l'étape
    if (etape === 'basic_info') {
      AnalyticsService.trackFormulaireBasicInfoVersDetails(organizerId, tempsPasseEtape, champsRemplis, erreursRencontrees, completudePourcentage, typeEvenement, userLocation || undefined);
    } else if (etape === 'details') {
      AnalyticsService.trackFormulaireDetailsVersPreview(organizerId, tempsPasseEtape, champsRemplis, erreursRencontrees, completudePourcentage, typeEvenement, userLocation || undefined);
    }
  };

  const trackCreationAbandoned = (
    etapeAbandon: 'basic_info' | 'details' | 'preview',
    dernierChampModifie: string,
    tempsTotalSession: number,
    completudeFinale: number,
    raisonProbable: 'erreur_validation' | 'fermeture_navigateur' | 'inactivite',
    champsRemplis: string[]
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackCreationEvenementAbandonnee(
      organizerContext.organizerProfile.id,
      etapeAbandon,
      dernierChampModifie,
      tempsTotalSession,
      completudeFinale,
      raisonProbable,
      champsRemplis,
      userLocation || undefined
    );
  };

  const trackRecurringEventConfigured = (
    frequence: 'daily' | 'weekly',
    typeFin: 'date' | 'occurrences',
    dureeSerie: number,
    nombreJoursSemaine?: number,
    nombreOccurrences?: number
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackEvenementRecurrentConfigure(
      organizerContext.organizerProfile.id,
      frequence,
      typeFin,
      dureeSerie,
      nombreJoursSemaine,
      nombreOccurrences,
      userLocation || undefined
    );
  };

  const trackDraftSaved = (
    draftId: string,
    etapeSauvegarde: 'basic_info' | 'details' | 'preview',
    completudePourcentage: number,
    champsRemplis: string[],
    estPremierDraft: boolean,
    tempsAvantSauvegarde: number
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackEvenementSauvegardeDraft(
      organizerContext.organizerProfile.id,
      draftId,
      etapeSauvegarde,
      completudePourcentage,
      champsRemplis,
      estPremierDraft,
      tempsAvantSauvegarde,
      userLocation || undefined
    );
  };

  const trackEventPublished = (
    evenementId: string,
    tempsTotalCreation: number,
    nombreSessionsCreation: number,
    etaitDraft: boolean,
    completudeFinale: number,
    typeEvenement: 'unique' | 'recurrent',
    nombreOccurrences?: number
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackEvenementPublie(
      organizerContext.organizerProfile.id,
      evenementId,
      tempsTotalCreation,
      nombreSessionsCreation,
      etaitDraft,
      completudeFinale,
      typeEvenement,
      nombreOccurrences,
      userLocation || undefined
    );
  };

  const trackOrganizerNavigation = (
    pageVisitee: 'events_actifs' | 'drafts' | 'events_passes' | 'profil' | 'statistiques' | 'creation',
    tempsPasse: number,
    actionsEffectuees: string[]
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackNavigationDashboardOrganisateur(
      organizerContext.organizerProfile.id,
      pageVisitee,
      tempsPasse,
      actionsEffectuees,
      userLocation || undefined
    );
  };

  const trackFailedPublishAttempt = (etape: 'basic_info' | 'details' | 'preview', champsMananquants: string[], completude: number, tempsAvantTentative: number, nombreTentatives: number) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    const organizerId = organizerContext.organizerProfile.id; // STOCKER L'ID AVANT LE setTimeout
    
    // AJOUTER UN PETIT DÉLAI pour laisser le temps à Firebase
    setTimeout(() => {
      AnalyticsService.trackTentativePublicationEchouee(
        organizerId, // UTILISER LA VARIABLE STOCKÉE
        etape, 
        champsMananquants, 
        completude, 
        tempsAvantTentative, 
        nombreTentatives, 
        userLocation || undefined
      );
    }, 200); // 200ms au lieu de 50ms
  };

  // ===== MÉTHODES PHASE 4 : ANALYTICS PROFIL ORGANISATEUR =====

  const trackProfileCompletionScore = (elementsCompletes: string[], elementsManquants: string[], score: number) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackProfilCompletionScore(
      organizerContext.organizerProfile.id,
      score,
      elementsCompletes,
      elementsManquants,
      userLocation || undefined
    );
  };

  const trackPublicProfileView = (organisateurConsulteId: string, elementsVisibles: string[], tempsConsultation: number) => {
    if (!isInitialized.current) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    const typeVisiteur = authContext?.user ? 'organisateur' : 'touriste';
    const estOrganisateurVisiteur = !!organizerContext?.organizerProfile?.id;
    
    AnalyticsService.trackProfilPublicConsulte(
      organisateurConsulteId,
      typeVisiteur,
      estOrganisateurVisiteur,
      elementsVisibles,
      tempsConsultation,
      userLocation || undefined
    );
  };

  const trackContactClick = (organisateurId: string, typeContact: 'email' | 'telephone' | 'site_web' | 'instagram', depuisVue: 'profil_public' | 'evenement_modal' | 'card_evenement') => {
    if (!isInitialized.current) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackContactProfilClic(
      organisateurId,
      typeContact,
      depuisVue,
      userLocation || undefined
    );
  };

  const trackImageUpload = (typeImage: 'avatar' | 'galerie', tailleFichier: number, formatImage: string, succesUpload: boolean, nombreImagesGalerieTotal?: number, tentativeNumero: number = 1) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;

    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackImageProfilUploadee(
      organizerContext.organizerProfile.id,
      typeImage,
      tailleFichier,
      formatImage,
      succesUpload,
      nombreImagesGalerieTotal,
      tentativeNumero,
      userLocation || undefined
    );
  };

  const trackFieldModified = (
    etape: 'basic_info' | 'details' | 'preview',
    champ: string,
    nombreModifications: number,
    valeurLongueur?: number,
    tentativesGeolocalisation?: number
  ) => {
    if (!isInitialized.current || !organizerContext?.organizerProfile?.id) return;
    
    const userLocation = ZoneGeographiqueService.creerLocalisationUtilisateur(geolocation.location);
    AnalyticsService.trackChampFormulaireModifie(
      organizerContext.organizerProfile.id,
      etape,
      champ,
      nombreModifications,
      valeurLongueur,
      tentativesGeolocalisation,
      userLocation || undefined
    );
  };

  // 🚀 OPTIMISATION CRITIQUE : Référence stable unique pour éviter re-renders
  const stableContextValue = useRef<AnalyticsContextType>();
  
  // Initialiser une seule fois avec des fonctions directement liées au service
  // IMPORTANT: Utiliser getFreshUserLocation() au lieu de geolocation.location
  // pour éviter le problème de closure stale (geoloc arrive souvent après le 1er render)
  if (!stableContextValue.current) {
    stableContextValue.current = {
      // Tracking des événements (Phase 1 - Touristes) - Direct service binding
      trackEventAppearance: (event: any, viewInterface: 'card' | 'carte_pin' | 'carte_preview', durationMs?: number) => {
        if (!isInitialized.current) return;
        const userLocation = getFreshUserLocation();
        AnalyticsService.trackApparitionEvenement(event, viewInterface, userLocation || null, durationMs);
      },

      trackEventClick: (event: any, viewInterface: 'card' | 'carte_pin' | 'carte_preview', timeSinceAppearance: number = 0) => {
        if (!isInitialized.current || !sessionInitialized.current) return;

        const debounceKey = `click-${event.id}-${viewInterface}`;
        debounceAnalytics(debounceKey, () => {
          const userLocation = getFreshUserLocation();
          AnalyticsService.trackClicEvenement(event, viewInterface, userLocation || null, timeSinceAppearance);
        });
      },

      trackEventConsultation: (event: any, duration: number, consultationDetails: any) => {
        if (!isInitialized.current || !sessionInitialized.current) return;
        const userLocation = getFreshUserLocation();
        AnalyticsService.trackConsultationEvenement(event, duration, consultationDetails, userLocation || null);
      },

      trackEventLike: (event: any, method: 'swipe_droite' | 'bouton_favori', context: 'card' | 'carte' | 'modale' | 'carte_preview') => {
        if (!isInitialized.current || !sessionInitialized.current) return;
        const userLocation = getFreshUserLocation();
        AnalyticsService.trackLikeEvenement(event, method, context, userLocation || null);
      },

      trackEventDismiss: (event: any, context: 'card' | 'carte' | 'modale' | 'carte_preview' = 'carte') => {
        if (!isInitialized.current) return;
        const userLocation = getFreshUserLocation();
        if (userLocation) {
          AnalyticsService.trackDismissEvenement(event, context, userLocation);
        }
      },

      trackNavigation: (event: any, type: 'directions' | 'website' | 'phone' | 'email' | 'calendar', context: 'card' | 'carte' | 'modale' | 'carte_preview') => {
        if (!isInitialized.current) return;
        const userLocation = getFreshUserLocation();
        if (userLocation) {
          AnalyticsService.trackNavigation(event, type, context, userLocation);
        }
      },

      trackSwipeAction: (event: any, type: 'swipe_droite' | 'swipe_gauche', context: 'card' | 'carte' | 'modale' | 'carte_preview') => {
        if (!isInitialized.current) return;
        const userLocation = getFreshUserLocation();
        if (userLocation) {
          AnalyticsService.trackSwipeAction(event, type, context, userLocation);
        }
      },

      trackPinClick: (pinLocation: { lat: number; lng: number }, eventsInPin: any[]) => {
        if (!isInitialized.current) return;
        const userLocation = getFreshUserLocation();
        if (userLocation) {
          AnalyticsService.trackPinClick(pinLocation, eventsInPin, userLocation);
        }
      },

      // Tracking des actions utilisateur (Phase 1) - Direct service binding
      markSearchPerformed: () => {
        if (!isInitialized.current) return;
        AnalyticsService.markSearchPerformed();
      },

      markFilterApplied: () => {
        if (!isInitialized.current) return;
        AnalyticsService.markFilterApplied();
      },

      trackSearch: (searchTerms: string, resultCount: number) => {
        if (!isInitialized.current) return;
        const debounceKey = `search-${searchTerms}`;
        debounceAnalytics(debounceKey, () => {
          const userLocation = getFreshUserLocation();
          AnalyticsService.trackRechercheEvenement(searchTerms, resultCount, userLocation || undefined);
        });
      },

      trackFilters: (filters: any, resultCount: number) => {
        if (!isInitialized.current) return;
        const filtersKey = JSON.stringify(filters);
        const debounceKey = `filters-${filtersKey}`;
        debounceAnalytics(debounceKey, () => {
          const userLocation = getFreshUserLocation();
          AnalyticsService.trackFiltreEvenement(filters, resultCount, userLocation || undefined);
        });
      },

      // Tracking UX : Vue, Partage, Affordance, Navigation
      trackViewModeChange: (newMode: 'list' | 'map', previousMode: 'list' | 'map') => {
        if (!isInitialized.current) return;
        AnalyticsService.trackViewModeChange(newMode, previousMode);
      },

      trackSwipeAffordanceShown: (context: 'card' | 'modal') => {
        if (!isInitialized.current) return;
        AnalyticsService.trackSwipeAffordanceShown(context);
      },

      trackSwipeAfterAffordance: (context: 'card' | 'modal', didSwipe: boolean) => {
        if (!isInitialized.current) return;
        AnalyticsService.trackSwipeAfterAffordance(context, didSwipe);
      },

      trackBottomNavClick: (item: string, action: 'open' | 'close' | 'navigate' | 'toggle') => {
        if (!isInitialized.current) return;
        AnalyticsService.trackBottomNavClick(item, action);
      },

      trackShareEvent: (event: any, method: 'native' | 'whatsapp' | 'clipboard', context: string) => {
        if (!isInitialized.current) return;
        AnalyticsService.trackShareEvent(event, method, context);
      },

      // Location Fallback Toast tracking
      trackLocationFallbackToastShown: () => {
        if (!isInitialized.current) return;
        AnalyticsService.trackLocationFallbackToastShown();
      },
      trackLocationFallbackToastAction: (action: 'locate_me' | 'dismiss' | 'auto_dismiss', result?: 'geolocation_success' | 'geolocation_denied') => {
        if (!isInitialized.current) return;
        AnalyticsService.trackLocationFallbackToastAction(action, result);
      },

      // Organisateur tracking - Utiliser les fonctions existantes
      trackCreationEventStart,
      trackFormStepCompleted,
      trackFieldModified,
      trackCreationAbandoned,
      trackRecurringEventConfigured,
      trackDraftSaved,
      trackEventPublished,
      trackOrganizerNavigation,
      trackFailedPublishAttempt,

      // Profil organisateur tracking
      trackProfileCompletionScore,
      trackPublicProfileView,
      trackContactClick,
      trackImageUpload,
    };
  }

  return (
    <AnalyticsContext.Provider value={stableContextValue.current}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// ✅ Hook simplifié qui utilise le contexte
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}; 